import { Component } from '@theme/component';

/**
 * Folds a string down to what a filter should compare: no case, no accents, no
 * runs of whitespace. NFD splits `í` into `i` plus a combining acute and the
 * regexp drops the mark. The content is Spanish and the design's own copy says
 * «envío»; nobody reaches for the accent key inside a search field.
 *
 * `ñ` folds to `n` as a side effect. That is wanted here -- typing "ano" and
 * finding «año» is the forgiving behaviour of a filter -- but it is worth
 * saying, because it is not what a dictionary would do.
 *
 * @param {string} value
 * @returns {string}
 */
function fold(value) {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * `hidden` takes the element out of the accessibility tree; the class is what
 * makes the display stick past the theme's own rules.
 *
 * @param {Element | null} element
 * @param {boolean} hidden
 */
function setHidden(element, hidden) {
  if (!element) return;
  element.classList.toggle('mm-faq-hidden', hidden);
  element.toggleAttribute('hidden', hidden);
}

/**
 * Filters the FAQ accordion rows already rendered on the page against what is
 * typed. It does not fetch and it does not navigate: the FAQ is a dozen rows of
 * text the page already paid for, so the fastest search is the one that never
 * leaves.
 *
 * @extends {Component}
 */
class MmFaqSearchComponent extends Component {
  requiredRefs = ['input'];

  connectedCallback() {
    super.connectedCallback();

    // Restoring from the back/forward cache, or a browser refilling the field,
    // leaves a value in the input that nothing has filtered yet.
    this.filter();

    document.addEventListener('shopify:section:load', this.#onSectionLoad);
    document.addEventListener('shopify:block:select', this.#onBlockSelect);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:section:load', this.#onSectionLoad);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    clearTimeout(this.#announceTimer);
    this.#topic = null;
    this.#restoreAll();
    super.disconnectedCallback();
  }

  /**
   * Fires only when this component's own subtree changed (component.js:91), so
   * it covers the field being re-rendered, never the questions. `super` is what
   * refreshes `refs`; skipping it leaves them pointing at detached nodes.
   */
  updatedCallback() {
    super.updatedCallback();
    this.filter();
  }

  /**
   * Escape empties the field, the way a search field is expected to.
   *
   * @param {KeyboardEvent} event
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;
    if (!this.refs.input?.value) return;

    event.preventDefault();
    this.clear();
  }

  /** The reset control, and what Escape runs. Focus goes back to the field. */
  clear() {
    const { input } = this.refs;
    if (!input) return;
    if (input.value === '' && !this.#topic) return;

    input.value = '';
    this.#topic = null;
    this.filter();
    input.focus();
  }

  /**
   * A topic chip. Toggling: pressing the one already applied clears it, which
   * is what an `aria-pressed` control is expected to do.
   *
   * Typing and picking a topic are the same filter with a different source, so
   * only one can be active. The field wins because it is what the visitor is
   * looking at while they type.
   *
   * @param {Event} event
   */
  pickTopic(event) {
    const button = event.target instanceof Element ? event.target.closest('.mm-faq-search__topic') : null;
    if (!button) return;

    this.#topic = this.#topic === button ? null : button;

    const { input } = this.refs;
    if (input) input.value = '';

    this.filter();
  }

  filter() {
    const raw = this.refs.input?.value ?? '';
    const query = fold(raw);
    const minChars = Number(this.dataset.mmMinChars || '2');
    // Below the floor the field behaves as if it were empty: one letter matches
    // nearly every question and the page just flickers.
    const typing = query.length >= minChars;
    const searchAnswers = this.dataset.mmSearchAnswers === 'true';

    // A topic carries a list, and any one of its terms matching is enough: the
    // chip says «rastrear» and the questions say «orden» and «envío».
    const topicTerms = this.#topic
      ? (this.#topic.dataset.mmTerms ?? '')
          .split(',')
          .map(fold)
          .filter(Boolean)
      : [];
    // A chip without terms stands for a group: the one whose title reads like
    // its label.
    const topicGroup = this.#topic && topicTerms.length === 0 ? fold(this.#topic.dataset.mmGroup ?? '') : '';
    const active = typing || topicTerms.length > 0 || topicGroup !== '';

    for (const chip of this.#chips()) {
      chip.setAttribute('aria-pressed', chip === this.#topic ? 'true' : 'false');
    }

    // The reset control follows anything that narrows the page, typed or picked.
    if (this.refs.clear) this.refs.clear.hidden = raw === '' && !this.#topic;

    /** @param {string} haystack */
    const hits = (haystack) =>
      topicTerms.length > 0 ? topicTerms.some((term) => haystack.includes(term)) : haystack.includes(query);

    let matches = 0;

    for (const accordion of this.#accordions()) {
      if (topicGroup) {
        const group = this.#groupOf(accordion);
        const title = fold(group.querySelector('h1, h2, h3, h4')?.textContent ?? '');
        const inGroup = title === topicGroup;

        for (const row of accordion.querySelectorAll('accordion-custom')) {
          const details = row.querySelector('details');
          if (details && !this.#openState.has(row)) this.#openState.set(row, details.open);
          setHidden(row, !inGroup);
          if (inGroup) matches += 1;
        }

        setHidden(group, !inGroup);
        continue;
      }

      let groupMatches = 0;

      for (const row of accordion.querySelectorAll('accordion-custom')) {
        const details = row.querySelector('details');
        if (!details) continue;

        // Remembered the first time the row is seen, so clearing the field puts
        // the accordion back the way the merchant configured it.
        if (!this.#openState.has(row)) this.#openState.set(row, details.open);

        const question = fold(details.querySelector('summary')?.textContent ?? '');
        const answer = searchAnswers ? fold(details.querySelector('.details-content')?.textContent ?? '') : '';

        const inQuestion = !active || hits(question);
        const inAnswer = !inQuestion && active && hits(answer);
        const hit = inQuestion || inAnswer;

        setHidden(row, !hit);
        if (!hit) continue;

        groupMatches += 1;
        matches += 1;

        if (!active) {
          details.open = this.#openState.get(row) ?? false;
        } else if (inAnswer) {
          // Otherwise the row reads as a false positive: nothing in the visible
          // question contains what was typed.
          details.open = true;
        }
      }

      setHidden(this.#groupOf(accordion), active && groupMatches === 0);
    }

    // The empty state names what was applied: the typed words, or the chip's
    // own label when the terms behind it are not what anybody typed.
    this.#report(matches, active ? raw || this.#topic?.dataset.mmLabel || '' : '');
  }

  /** @returns {HTMLElement[]} */
  #chips() {
    const { topics } = this.refs;
    if (!topics) return [];
    return Array.isArray(topics) ? /** @type {HTMLElement[]} */ (topics) : [/** @type {HTMLElement} */ (topics)];
  }

  /**
   * The accordions to filter. `page` reaches into `#MainContent`
   * (layout/theme.liquid:154) and not the document, because `blocks/menu.liquid`
   * renders the phone footer menu with the same `.accordion` class and filtering
   * the footer is not what anyone asked for.
   *
   * @returns {NodeListOf<Element>}
   */
  #accordions() {
    const scope =
      this.dataset.mmScope === 'section'
        ? this.closest('.shopify-section')
        : document.getElementById('MainContent');

    return (scope ?? document).querySelectorAll('.accordion');
  }

  /**
   * The category to hide with the accordion. The heading is a SIBLING of the
   * accordion, so this is the nearest native group -- `.group-block` comes from
   * `snippets/group.liquid:26` and is not an id, which rule 10 says the editor
   * regenerates on duplicate.
   *
   * @param {Element} accordion
   * @returns {Element}
   */
  #groupOf(accordion) {
    const group = accordion.closest('.group-block');
    if (!group) return accordion;

    // More than one accordion inside means the climb went past the category and
    // landed on a wrapper around the whole page. Hiding that blanks the section.
    if (group.querySelectorAll('.accordion').length !== 1) return accordion;

    return group;
  }

  #restoreAll() {
    for (const accordion of this.#accordions()) {
      setHidden(this.#groupOf(accordion), false);
      for (const row of accordion.querySelectorAll('accordion-custom')) setHidden(row, false);
    }
  }

  /**
   * @param {number} matches
   * @param {string} terms - Empty while the query is below the floor.
   */
  #report(matches, terms) {
    const { empty, status } = this.refs;
    const emptyText = (this.dataset.mmEmpty ?? '').replace('%%TERMS%%', terms);

    if (empty) {
      empty.hidden = !(terms && matches === 0);
      if (!empty.hidden) empty.textContent = emptyText;
    }

    if (!status) return;

    let message = '';
    if (terms) {
      if (matches === 0) message = emptyText;
      else if (matches === 1) message = this.dataset.mmCountOne ?? '';
      else message = (this.dataset.mmCountOther ?? '').replace('%%COUNT%%', String(matches));
    }

    // Filtering is synchronous; only the announcement waits, so a screen reader
    // is not read one count per keystroke.
    clearTimeout(this.#announceTimer);
    this.#announceTimer = setTimeout(() => {
      // Clear-then-set: a live region already holding the same string does not
      // fire again, and one more letter often lands on the same count.
      status.textContent = '';
      requestAnimationFrame(() => {
        status.textContent = message;
      });
    }, 250);
  }

  /** A re-render of the section that owns the questions never reaches us. */
  #onSectionLoad = () => this.filter();

  /** If the editor selects a row this filter hid, it cannot scroll to it. */
  #onBlockSelect = () => {
    const { input } = this.refs;
    if (!input) return;
    if (input.value === '' && !this.#topic) return;
    input.value = '';
    this.#topic = null;
    this.filter();
  };

  /** The chip currently applied, if any. Null while the field is in charge. */
  /** @type {HTMLElement | null} */
  #topic = null;

  /** @type {Map<Element, boolean>} */
  #openState = new Map();

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #announceTimer;
}

if (!customElements.get('mm-faq-search-component')) {
  customElements.define('mm-faq-search-component', MmFaqSearchComponent);
}
